import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { tap } from "rxjs/operators";
import { SIGNING_AGENT_DESIGNATION_LABELS, SigningAgentDesignation } from "../../../shared/enums/signingAgentDesignation";
import { SigningAgentDesignationAccount } from "../../../shared/models/signing-agent-designation-account.model";
import { SigningAgentDesignationService } from "../../../shared/services/signing-agent-designation.service";
import { AppDialogModule } from "../../../shared/components/app-dialog/app-dialog.component";
import { AppSpinnerModule } from "../../../shared/modules/app-spinner.module";
import { SupportUsersTab } from "../../models/support-users-tab";

export type SelectValueHolder = { value: unknown };

@Component({
    selector: 'app-signing-agent-designation-table-display',
    templateUrl: 'signing-agent-designation-table-display.component.html',
    styleUrls: ['signing-agent-designation-table-display.component.scss'],
    standalone: false
})

export class SigningAgentDesignationTableDisplayComponent {
  @Input() userList: SigningAgentDesignationAccount[] = [];
  @Input() activeTab: SupportUsersTab = 'client';
  @Input() loadFailed = false;
  @Output() designationsChanged = new EventEmitter<void>();
  sortAscending = true;
  sortProperty: string;

  designations = [
    { key: SigningAgentDesignation.TeamSigningAgent, value: SIGNING_AGENT_DESIGNATION_LABELS[SigningAgentDesignation.TeamSigningAgent] },
    { key: SigningAgentDesignation.PreferredAgent, value: SIGNING_AGENT_DESIGNATION_LABELS[SigningAgentDesignation.PreferredAgent] }
  ];

  constructor(
    private readonly designationService: SigningAgentDesignationService,
    private readonly toastr: ToastrService,
    private readonly spinner: AppSpinnerModule,
    private readonly dialog: AppDialogModule
  ) { }

  sortDesignationAccounts(property: string): SigningAgentDesignationAccount[] {
    this.updateSortProperty(property);
    return this.userList.sort((a, b) => {
      const valueA = (typeof a[property] === 'string') ? a[property].toLocaleLowerCase() : a[property];
      const valueB = (typeof b[property] === 'string') ? b[property].toLocaleLowerCase() : b[property];
      if (valueA < valueB) {
        return this.sortAscending ? -1 : 1;
      } else if (valueA > valueB) {
        return this.sortAscending ? 1 : -1;
      } else {
        return 0;
      }
    });
  }

  updateSortProperty(property: string) {
    if (property === this.sortProperty) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.sortAscending = true;
    }
    this.sortProperty = property;
  }

  getSelectedDesignation(user: SigningAgentDesignationAccount): string | null {
    return user?.designationCode ?? null;
  }

  getDesignationDisplayValue(user: SigningAgentDesignationAccount): string {
    const code = this.getSelectedDesignation(user);
    if (!code) {
      return "";
    }
    return SIGNING_AGENT_DESIGNATION_LABELS[code] ?? code;
  }

  setDesignation(
    user: SigningAgentDesignationAccount,
    designationCode: SigningAgentDesignation,
    select?: SelectValueHolder
  ): void {
    this.spinner.show();

    this.designationService.putDesignationByAccountId(user.accountId, designationCode)
      .pipe(
        tap(() => {
          this.spinner.hide();
          this.toastr.success("Designation updated successfully.");
          this.designationsChanged.emit();
        })
      )
      .subscribe({
        error: () => {
          this.spinner.hide();
          this.toastr.error("Failed to update designation.");
          this.revertSelect(user, select);
        }
      });
  }

  onDelete(user: SigningAgentDesignationAccount): void {
    this.spinner.show();

    this.designationService
      .deleteDesignationByAccountId(user.accountId)
      .pipe(
        tap(() => {
          this.spinner.hide();
          this.toastr.success("Designation removed successfully.");
          this.designationsChanged.emit();
        })
      )
      .subscribe({
        error: () => {
          this.spinner.hide();
          this.toastr.error("Failed to remove designation.");
        }
      });
  }

  deleteConfirmationDialog(user: SigningAgentDesignationAccount) {
    this.dialog.show({
      title: "Delete User's Designation",
      message: `Are you sure you want to delete the designation for ${user.email}?`,
      noButtonText: "Cancel",
      yesButtonText: "Delete",
    }).result.then((dialogResult) => {
      if (dialogResult === "yes") {
        this.onDelete(user);
      }
    });
  }

  private revertSelect(user: SigningAgentDesignationAccount, select?: SelectValueHolder): void {
    if (select) {
      select.value = this.getSelectedDesignation(user);
    }
  }
}
